import { Timestamp } from "firebase/firestore";
import { type FirebaseStorage } from "firebase/storage";

import { readCmsTimestampMillis } from "@repo/common";
import { type AudioLessonCMS } from "@repo/common/src/types/firestore/cms/audioCourse";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";

import { assertLessonDurationWithinCap } from "./assertLessonDurationWithinCap";
import { copyFile } from "./copyFile";
import { deleteFile } from "./deleteFile";
import { getAudioDuration } from "./getAudioDuration";

type FileType = "audio" | "homework";

interface AudioFileOperationsProps<T extends AudioLessonCMS> {
  file: T;
  savedValuesOfFileInDB: T | undefined;
  valuesOnSave: T;
  storage: FirebaseStorage;
  isPremium: boolean;
}

const resolveTargetPath = (filePath: string, isPremium: boolean, fileType: FileType): string => {
  const { premiumPath, freePath } = getStoragePaths(fileType);
  const currentPath = filePath.includes(premiumPath) ? premiumPath : freePath;
  const targetPath = isPremium ? premiumPath : freePath;

  if (currentPath === targetPath) {
    return filePath;
  }

  return filePath.replace(currentPath, targetPath);
};

/**
 * Move audio file to the correct storage path, calculate its duration, and add/update timestamps.
 * Duration is validated before any destructive storage move.
 */
export async function fileOperationsAudioCourse<T extends AudioLessonCMS>({
  file,
  savedValuesOfFileInDB,
  valuesOnSave,
  storage,
  isPremium,
}: AudioFileOperationsProps<T>): Promise<T & { duration: number }> {
  const updatedFileValues = { ...savedValuesOfFileInDB, ...valuesOnSave, premium: isPremium } as T;

  const sourceAudioPath = file.file;
  const targetAudioPath = resolveTargetPath(sourceAudioPath, isPremium, "audio");
  const durationProbePath = targetAudioPath === sourceAudioPath ? sourceAudioPath : sourceAudioPath;

  // Validate against the source file before any copy/delete.
  const duration = await getAudioDuration(durationProbePath, storage);
  assertLessonDurationWithinCap(duration);

  let homeworkTargetPath: string | undefined;
  if ("homework_file" in updatedFileValues && updatedFileValues.homework_file) {
    homeworkTargetPath = resolveTargetPath(updatedFileValues.homework_file, isPremium, "homework");
  }

  // Destructive moves only after all validations succeed.
  if (targetAudioPath !== sourceAudioPath) {
    await copyFile({ storage, oldPath: sourceAudioPath, newPath: targetAudioPath });
    await deleteFile({ path: sourceAudioPath, storage });
  }
  updatedFileValues.file = targetAudioPath;

  if (
    "homework_file" in updatedFileValues &&
    updatedFileValues.homework_file &&
    homeworkTargetPath &&
    homeworkTargetPath !== updatedFileValues.homework_file
  ) {
    await copyFile({
      storage,
      oldPath: updatedFileValues.homework_file,
      newPath: homeworkTargetPath,
    });
    await deleteFile({ path: updatedFileValues.homework_file, storage });
    updatedFileValues.homework_file = homeworkTargetPath;
  }

  const now = Timestamp.now().toMillis();

  if (!updatedFileValues.created_on_milis) {
    updatedFileValues.created_on_milis = now;
  }

  if (shouldUpdateTimestamp(savedValuesOfFileInDB, updatedFileValues)) {
    updatedFileValues.updated_on_milis = now;
  }

  persistPublishDateMilis(updatedFileValues, valuesOnSave.publish_date);

  return { ...updatedFileValues, duration };
}

/**
 * Persist only `publish_date_milis`. Never write `publish_date` into `files[]` —
 * a JS Date lands as a map, and a Timestamp is leftover dual-write.
 */
function persistPublishDateMilis<T extends AudioLessonCMS>(file: T, pickerValue: unknown): void {
  const publishMs = readCmsTimestampMillis(pickerValue);

  delete file.publish_date;

  if (publishMs === null) {
    delete file.publish_date_milis;
    return;
  }

  file.publish_date_milis = publishMs;
}

function getStoragePaths(fileType: FileType): { premiumPath: string; freePath: string } {
  if (fileType === "audio") {
    return {
      premiumPath: StoragePaths.AudioCoursesPremium,
      freePath: StoragePaths.AudioCoursesFree,
    };
  } else {
    return {
      premiumPath: StoragePaths.AudioCoursesHomeworkPremium,
      freePath: StoragePaths.AudioCoursesHomeworkFree,
    };
  }
}

function hasFileChanged<T extends AudioLessonCMS>(originalFile: T | undefined, updatedFile: T): boolean {
  if (!originalFile) return true;

  const audioFileChanged = originalFile.file !== updatedFile.file;

  const homeworkFileChanged =
    "homework_file" in originalFile &&
    "homework_file" in updatedFile &&
    originalFile.homework_file !== updatedFile.homework_file;

  return audioFileChanged || homeworkFileChanged;
}

function shouldUpdateTimestamp<T extends AudioLessonCMS>(originalFileValues: T | undefined, updatedFile: T): boolean {
  return hasFileChanged(originalFileValues, updatedFile) || !updatedFile.updated_on_milis;
}
